/**
 * ArRow Component
 *
 * Renders a single row in the ArTable using CSS Grid.
 * Subscribes only to row-level metadata (`locked`, `annotation`) so UI can respond
 * immediately to locking and annotation changes without re-rendering on cell edits.
 * Sole owner of the row id's `useSortable` registration: the drag handle lives in
 * `ArRowCheckbox`, which receives the activator props rather than registering the
 * same id a second time (SYD-2406).
 *
 * Also the single place that asks whether a Form Derive projection owns this row,
 * for the row-level surfaces of SYD-2404: the 2px gutter rule, the absent
 * drag handle on a mirror projection, the projected cell's hover line, and the
 * composite sub-fields the projection fills inside a cell it does not own whole.
 * Ownership comes from the `drv~` row-id namespace, never from `deriveOwner`.
 *
 * The even-row shade is `colorFillQuaternary`, which `ThemeProvider` pins to an
 * opaque FIELD_ALT. `ArRowCheckbox` and `ArCell` must resolve the stripe from
 * that same token: while they disagreed, the checkbox gutter showed a seam in
 * light mode and lost the stripe entirely in dark, where the row's old
 * `colorBgLayout` and the page behind it are both black (SYD-2439).
 *
 * In PDF export the wrapper holding the row and its annotation strip avoids a
 * page break inside it, as `ArBundleHeaderRow` does: Chromium fragments the
 * nested subgrid badly, printing a split row's tail over its annotation (SYD-2900).
 *
 * In card mode (below `md`) the same subgrid items lay out as a card over the
 * body's two-track template: checkbox and grip top left, the actions menu top
 * right, then each column's label stacked above its cell, both spanning the
 * card's full width so a wide editor is never squeezed into half of it, and the
 * annotation spanning both tracks as the card footer. A member card is indented
 * rather than ruled. Every card placement is a `gridColumnStart`/`gridColumnEnd`
 * longhand: `ArRowActions` sets `gridColumnEnd` in table mode, and React clearing
 * a `gridColumn` shorthand on the way back to the table would wipe it.
 */

import { FC, memo, JSX, useContext, useMemo } from 'react';
import { theme, Typography } from 'antd';
import { usePdfExportMode } from '../usePdfExportMode';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { DraggableAttributes, DraggableSyntheticListeners } from '@dnd-kit/core';
import { proxy, useSnapshot } from 'valtio';
import ArCell from './ArCell';
import ArRowAnnotation from './ArRowAnnotation';
import ArRowActions from '../actions/ArRowActions';
import ArRowContextMenu from '../actions/ArRowContextMenu';
import { ArRowCheckbox } from '../selection';
import { useStoreNonReactive } from '@/store';
import type { CollectionState } from '@/store';
import { ArTableUIStateContext } from '../context';
import { FieldTypeContext } from '@/providers/FieldTypeContext';
import resolveArColumnLabel from '../column/resolveArColumnLabel';
import {
  deriveCellTooltipOfColumn,
  deriveProjectionOfRow,
  deriveSubFieldsByColumn,
  type ArTableDeriveProjection,
} from '../derives';
import type { ArBundleType, ArColumnDefinition, ArRowData, ArRowId, TypeId } from '#types/System.Artifacts.types';

type ArRowProps = {
  rowId: ArRowId;
  rowIndex: number;
  disabled?: boolean;
  stickyFirstColumn?: boolean;
  frozenLeftCount?: number;
  frozenLeftOffsetsByIndex?: number[];
  manualReorderEnabled?: boolean;
  columns: ArColumnDefinition[];
  protectedTypeIds?: ReadonlySet<TypeId>;
  formulaTypeIds?: ReadonlySet<TypeId>;
  formulaSubFieldsByColumn?: ReadonlyMap<TypeId, ReadonlySet<string>>;
  bundleRole?: 'member';
  bundleId?: string;
  bundleType?: ArBundleType;
  cardMode?: boolean;
};

type ArRowContentProps = {
  rowId: ArRowId;
  rowIndex: number;
  /** Resolved by `ArRow`, which paints the card wrapper with the same colour in card mode. */
  rowBackground: string;
  isLocked: boolean;
  annotation: string | undefined;
  disabled: boolean;
  stickyFirstColumn: boolean;
  frozenLeftCount: number;
  frozenLeftOffsetsByIndex: number[];
  manualReorderEnabled: boolean;
  columns: ArColumnDefinition[];
  protectedTypeIds: ReadonlySet<TypeId>;
  formulaTypeIds: ReadonlySet<TypeId>;
  formulaSubFieldsByColumn: ReadonlyMap<TypeId, ReadonlySet<string>>;
  /**
   * The Form Derive projection owning this row, or `null`. Draws the gutter rule,
   * and names the hover line of every cell it fills — one projection fills many
   * columns, so the line is resolved per cell rather than handed down whole.
   */
  deriveProjection: ArTableDeriveProjection | null;
  /**
   * The composite sub-fields that projection fills, per column, each mapped to the
   * sentence saying why it is locked. A dotted column carries no `_calculated`
   * marker, so this map is what stops the person editing the sub-field the derive
   * owns while its siblings stay theirs — and what tells them why.
   */
  deriveSubFieldsByColumnId: ReadonlyMap<TypeId, ReadonlyMap<string, string>>;
  dragAttributes: DraggableAttributes;
  dragListeners: DraggableSyntheticListeners;
  bundleRole?: 'member';
  bundleId?: string;
  bundleType?: ArBundleType;
  cardMode: boolean;
};

const ArRowContent: FC<ArRowContentProps> = memo(({
  rowId,
  rowIndex,
  rowBackground,
  isLocked,
  annotation,
  disabled,
  stickyFirstColumn,
  frozenLeftCount,
  frozenLeftOffsetsByIndex,
  manualReorderEnabled,
  columns,
  protectedTypeIds,
  formulaTypeIds,
  formulaSubFieldsByColumn,
  deriveProjection,
  deriveSubFieldsByColumnId,
  dragAttributes,
  dragListeners,
  bundleRole,
  bundleId,
  bundleType,
  cardMode,
}) => {
  const { token } = theme.useToken();
  const { fieldTypes } = useContext(FieldTypeContext);
  const pdfExportMode = usePdfExportMode();
  const isOddRow = rowIndex % 2 === 1;

  const cells: JSX.Element[] = [];

  for (let i = 0; i < columns.length; i++) {
    const column = columns[i];

    if (cardMode) {
      cells.push(
        <Typography.Text
          key={`${rowId}-${column.typeId}-label`}
          type="secondary"
          style={{ gridColumnStart: 1, gridColumnEnd: -1 }}
        >
          {resolveArColumnLabel(column, fieldTypes)}
        </Typography.Text>
      );
    }
    cells.push(
      <ArCell
        key={`${rowId}-${column.typeId}`}
        column={column}
        rowId={rowId}
        disabled={disabled}
        columnIndex={i}
        stickyFirstColumn={stickyFirstColumn}
        frozenLeftCount={frozenLeftCount}
        frozenLeftOffsetsByIndex={frozenLeftOffsetsByIndex}
        isOddRow={isOddRow}
        isRowLocked={isLocked}
        isColumnProtected={protectedTypeIds.has(column.typeId)}
        isFormulaColumn={formulaTypeIds.has(column.typeId)}
        formulaSubFields={formulaSubFieldsByColumn.get(column.typeId)}
        derivedCellTooltip={
          deriveProjection === null
            ? undefined
            : deriveCellTooltipOfColumn(deriveProjection, column.typeId) ?? undefined
        }
        derivedSubFields={deriveSubFieldsByColumnId.get(column.typeId)}
        bundleRole={bundleRole}
        bundleType={bundleType}
        cardMode={cardMode}
      />
    );
  }

  /**
   * The gutter rule. An inset shadow rather than a border, so a derive-owned row
   * keeps every column aligned with the manual rows above and below it.
   */
  const deriveGutterRule = deriveProjection !== null
    ? `inset 2px 0 0 ${token.colorPrimaryBorder}`
    : undefined;

  return (
    <>
      <div
        aria-label={`artable-row-${rowId}`}
        role="row"
        data-derive-owned={deriveProjection !== null ? 'true' : undefined}
        style={{
          display: 'grid',
          gridTemplateColumns: 'subgrid',
          gridColumn: '1 / -1',
          minHeight: token.controlHeight,
          borderBottom: annotation || cardMode ? undefined : `1px solid ${token.colorBorderSecondary}`,
          position: 'relative',
          backgroundColor: rowBackground,
          boxShadow: deriveGutterRule,
        }}
      >
        {!pdfExportMode && (
          <ArRowCheckbox
            rowId={rowId}
            isOddRow={isOddRow}
            isRowLocked={isLocked}
            stickyFirstColumn={stickyFirstColumn}
            manualReorderEnabled={manualReorderEnabled}
            indented={bundleRole === 'member' && !cardMode}
            dragAttributes={dragAttributes}
            dragListeners={dragListeners}
            cardMode={cardMode}
          />
        )}
        {cells}
        {!pdfExportMode && (
          <ArRowActions
            rowId={rowId}
            isLocked={isLocked}
            annotation={annotation}
            disabled={disabled}
            style={
              cardMode
                ? {
                    backgroundColor: rowBackground,
                    gridRow: 1,
                    justifySelf: 'end',
                    position: 'static',
                    borderLeft: 'none',
                    backdropFilter: 'none',
                  }
                : { backgroundColor: rowBackground }
            }
            bundleRole={bundleRole}
            bundleId={bundleId}
            bundleType={bundleType}
          />
        )}
      </div>
      {annotation !== undefined && (
        <ArRowAnnotation
          rowId={rowId}
          annotation={annotation}
          isLocked={isLocked}
          disabled={disabled}
          rowBackground={rowBackground}
        />
      )}
    </>
  );
});

/**
 * ArRow renders a row of cells based on column definitions.
 */
const emptyProtectedSet: ReadonlySet<TypeId> = new Set<TypeId>();
const emptyFormulaSet: ReadonlySet<TypeId> = new Set<TypeId>();
const emptyFormulaSubFieldsByColumn: ReadonlyMap<TypeId, ReadonlySet<string>> = new Map();
/** For the rows rendered outside an ArTable UI state (agent review, PDF portals). */
const EMPTY_DERIVE_PROJECTIONS = proxy<ArTableDeriveProjection[]>([]);

const ArRow: FC<ArRowProps> = ({
  rowId,
  rowIndex,
  disabled = false,
  stickyFirstColumn = true,
  frozenLeftCount = 0,
  frozenLeftOffsetsByIndex = [],
  manualReorderEnabled = false,
  columns,
  protectedTypeIds = emptyProtectedSet,
  formulaTypeIds = emptyFormulaSet,
  formulaSubFieldsByColumn = emptyFormulaSubFieldsByColumn,
  bundleRole,
  bundleId,
  bundleType,
  cardMode = false,
}) => {
  const { token } = theme.useToken();
  const pdfExportMode = usePdfExportMode();
  const { state: rowsStateRaw } = useStoreNonReactive('rows');
  const rowsState = rowsStateRaw as CollectionState<ArRowData>;
  const rowEntrySnap = useSnapshot(rowsState.docs.get(rowId) as { data: ArRowData });
  const uiState = useContext(ArTableUIStateContext);
  const deriveProjectionsSnap = useSnapshot(uiState?.deriveProjections ?? EMPTY_DERIVE_PROJECTIONS);
  const deriveProjection = deriveProjectionOfRow(deriveProjectionsSnap, rowId);
  /**
   * Keyed on the projection so the map's identity holds while the configuration
   * does — a fresh `Set` per render would defeat `ArCell`'s own `memo` on every
   * cell of every projected row.
   */
  const deriveSubFieldsByColumnId = useMemo(
    () => deriveSubFieldsByColumn(deriveProjection),
    [deriveProjection],
  );

  const isLocked = rowEntrySnap.data?.locked === true;
  const annotation = rowEntrySnap.data?.annotation;
  const rowBackground = isLocked
    ? token.colorFillSecondary
    : rowIndex % 2 === 1
      ? token.colorBgBase
      : token.colorFillQuaternary;
  /**
   * Two gates, deliberately different: the drag itself is off on a locked row,
   * while the grip is still handed down (`manualReorderEnabled && !disabled`) so
   * it renders disabled rather than vanishing — `ArRowCheckbox` reads the same
   * lock for its `disabled`, cursor and colour, and `ArBundleHeaderRow` splits
   * the two the same way (SYD-2406).
   *
   * A projected row keeps its grip. Row order is the person's on every projection
   * the subsystem still has — the rows are one per distinct value of a partition
   * key, and nothing in a reconcile pass rewrites their `order`.
   */
  const canManualReorder = manualReorderEnabled && !disabled && !isLocked;

  /**
   * `disabled` is stated as an object because a locked or read-only row must stay
   * a drop target while it cannot be picked up. `@dnd-kit/sortable`'s
   * `normalizeLocalDisabled` reads a bare boolean as
   * `{ draggable: value, droppable: false }` under a branch it labels "Backwards
   * compatibility", so the shape we want is unpinned until it is written out.
   */
  const { setNodeRef, transform, transition, isDragging, attributes, listeners } = useSortable({
    id: rowId,
    disabled: { draggable: !canManualReorder, droppable: false },
  });

  return (
    <ArRowContextMenu
      rowId={rowId}
      disabled={disabled}
      isLocked={isLocked}
      annotation={annotation}
      bundleRole={bundleRole}
      bundleId={bundleId}
      bundleType={bundleType}
    >
      <div
        ref={setNodeRef}
        style={{
          display: 'grid',
          gridTemplateColumns: 'subgrid',
          gridColumn: '1 / -1',
          transform: CSS.Transform.toString(transform),
          transition,
          opacity: isDragging ? 0.7 : 1,
          boxShadow: isDragging ? token.boxShadowSecondary : undefined,
          breakInside: pdfExportMode ? 'avoid' : undefined,
          pageBreakInside: pdfExportMode ? 'avoid' : undefined,
          ...(cardMode
            ? {
                border: `1px solid ${token.colorBorderSecondary}`,
                borderRadius: token.borderRadiusLG,
                padding: token.paddingXS,
                rowGap: token.paddingXXS,
                overflow: 'hidden',
                marginInlineStart: bundleRole === 'member' ? token.marginLG : undefined,
                backgroundColor: rowBackground,
              }
            : null),
        }}
      >
        <ArRowContent
          rowId={rowId}
          rowIndex={rowIndex}
          rowBackground={rowBackground}
          isLocked={isLocked}
          annotation={annotation}
          disabled={disabled}
          stickyFirstColumn={stickyFirstColumn}
          frozenLeftCount={frozenLeftCount}
          frozenLeftOffsetsByIndex={frozenLeftOffsetsByIndex}
          manualReorderEnabled={manualReorderEnabled && !disabled}
          columns={columns}
          protectedTypeIds={protectedTypeIds}
          formulaTypeIds={formulaTypeIds}
          formulaSubFieldsByColumn={formulaSubFieldsByColumn}
          deriveProjection={deriveProjection}
          deriveSubFieldsByColumnId={deriveSubFieldsByColumnId}
          dragAttributes={attributes}
          dragListeners={listeners}
          bundleRole={bundleRole}
          bundleId={bundleId}
          bundleType={bundleType}
          cardMode={cardMode}
        />
      </div>
    </ArRowContextMenu>
  );
};

export default memo(ArRow);
